import { ViewModelFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import { CalendarState } from '../../controller';
import {
  formatRfcTimeStringToTimeSlotView,
  formatShortDate,
} from '../../../../utils/dateAndTime/dateAndTime';
import { Optional } from '../../../../types/types';
import { MemoizedViewModalFactory } from '../viewModel';

export enum DialogState {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
}

type BaseDialogViewModel<Type, Data> = {
  isOpen: boolean;
  state: DialogState;
  data: Data;
  type: Type;
};

export type GeneralDialogViewModel = BaseDialogViewModel<
  Omit<DialogType, 'ServicesPreferences'>,
  {
    titleText?: string;
    contentText: string;
    confirmButtonText: string;
    confirmButtonUrlLink?: string;
    closeButtonText?: string;
  }
>;

export type ServicesPreferencesDialogViewModel = BaseDialogViewModel<
  DialogType.ServicesPreferences,
  Required<CalendarState>['servicesPreferencesModalData']
>;

export type DialogViewModel =
  | GeneralDialogViewModel
  | ServicesPreferencesDialogViewModel;

// eslint-disable-next-line no-shadow
export enum DialogType {
  RescheduleConfirm = 'reschedule-confirm',
  JoinWaitlist = 'join-waitlist',
  PremiumViewer = 'premium-viewer',
  siteIsNotEcom = 'non-ecom-site',
  ServicesPreferences = 'services-preferences',
}

export const MOBILE_APP_INVITE_LENDING_PAGE_URL_PREFIX =
  'https://apps.wix.com/place-invites/join-lp/';

export const memoizedDialogViewModel: MemoizedViewModalFactory<DialogViewModel> =
  {
    dependencies: {
      state: ['dialog', 'selectedTime', 'servicesPreferencesModalData'],
    },
    createViewModel: createDialogViewModel,
  };

export function createDialogViewModel({
  state,
  context,
}: ViewModelFactoryParams<CalendarState, CalendarContext>): DialogViewModel {
  const { dialog } = state;

  if (!dialog) {
    return {
      isOpen: false,
      state: DialogState.IDLE,
      data: { titleText: '', contentText: '', confirmButtonText: '' },
      type: DialogType.JoinWaitlist,
    };
  }

  const { type } = dialog;

  if (type === DialogType.ServicesPreferences) {
    return {
      data: state.servicesPreferencesModalData!,
      isOpen: true,
      state: DialogState.IDLE,
      type,
    };
  }

  const confirmButtonUrlLink = getUrl(dialog?.type, context);

  return {
    isOpen: true,
    state: dialog?.state || DialogState.IDLE,
    data: {
      ...getTexts({ state, context }),
      ...(confirmButtonUrlLink ? { confirmButtonUrlLink } : {}),
    },
    type,
  };
}

function getTexts({
  state,
  context,
}: ViewModelFactoryParams<CalendarState, CalendarContext>) {
  const { dialog, selectedTime } = state;
  const { t, businessInfo, experiments } = context;

  switch (dialog?.type) {
    case DialogType.RescheduleConfirm:
      return {
        titleText: t('app.rescheduled-booking.dialog.title'),
        contentText: t('app.rescheduled-booking.dialog.content', {
          date: formatShortDate(
            selectedTime!,
            businessInfo!.dateRegionalSettingsLocale,
          ),
          time: formatRfcTimeStringToTimeSlotView(
            selectedTime!,
            businessInfo!.dateRegionalSettingsLocale,
          ),
        }),
        confirmButtonText: t('app.rescheduled-booking.dialog.button'),
      };
    case DialogType.JoinWaitlist:
      return {
        titleText: t('app.dialog.join-waitlist.title'),
        contentText: t('app.dialog.join-waitlist.content'),
        confirmButtonText: t('app.dialog.join-waitlist.button'),
        closeButtonText: experiments.enabled(
          'specs.bookings.calendar.dialog.closeButton',
        )
          ? t('app.dialog.join-waitlist.close-button')
          : undefined,
      };
    case DialogType.PremiumViewer:
    case DialogType.siteIsNotEcom:
      return {
        contentText: t('app.dialog.premium-viewer.content'),
        confirmButtonText: t('app.dialog.premium-viewer.button'),
      };
    default:
      return {
        titleText: '',
        contentText: '',
        confirmButtonText: '',
      };
  }
}

const getUrl = (dialogType: Optional<DialogType>, context: CalendarContext) => {
  const metaSiteId = context.experiments.enabled(
    'specs.bookings.useOneLinerInsteadOfWixSDK',
  )
    ? context.flowAPI.controllerConfig.platformAPIs.bi?.metaSiteId
    : context.wixSdkAdapter.getMetaSiteId();
  return dialogType === DialogType.JoinWaitlist
    ? MOBILE_APP_INVITE_LENDING_PAGE_URL_PREFIX + metaSiteId
    : '';
};
